/*
  Список действующих ссылок в карточке счёта — §3.8.2.

  Отделён от блока выдачи не ради длины файла: выдача - это форма с состоянием
  и запросами, а здесь только показ того, что уже выдано. Смешивать их значит
  держать разметку таблицы в одном файле с обработчиками кнопок.

  Показываются только действующие ссылки: оператору нужен ответ на вопрос «что
  сейчас открывает этот счёт», а не архив выдач. Архив живёт в аудит-следе,
  откуда его не стереть.
*/

import { dateTime } from '../api/format.js'
import { Empty } from './Common.jsx'
import PageBar from './PageBar.jsx'

// `links` - одна страница действующих ссылок, `paging` - свойства панели: страницы режет
// сервер (21.10.2026, `usePagedList` у владельца списка, у `DebtorLink`).
export default function DebtorLinkList({ links, paging, onClose, onReissue, busy }) {
  if (paging.total === 0) {
    return (
      <div>
        <Empty title="Действующих ссылок нет">
          Здесь появятся выданные ссылки: кому, до какого числа действует и сколько раз открыта.
          Закрытые и истёкшие не показываются - их след остаётся в аудите.
        </Empty>
      </div>
    )
  }

  return (
    <div>
      {/*
        Предупреждение стоит ДО нажатия, а не после (решение владельца
        09.09.2026). «Выдать заново» закрывает прежнюю ссылку, и человек должен
        понимать это заранее: если он уже отправил её должнику сообщением, тот
        откроет страницу и увидит отказ. Сообщение постфактум объясняло бы
        последствие, которое уже наступило.
      */}
      <p className="muted">
        «Выдать заново» заменяет ссылку: прежняя перестанет открывать страницу сразу, а новая ляжет
        в буфер обмена. Отправляйте должнику именно её. Ссылку, напечатанную в уведомлении, заменить
        нельзя - письмо с ней могло уже уйти; закрывать её стоит, только если письмо попало не тому.
      </p>
      <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Кому</th>
              {/*
              Времени выдачи в таблице нет намеренно: оператору нужен срок, до
              которого ссылка работает, а «когда выдали» лежит в аудит-следе,
              откуда его не стереть. Пятая колонка в этой ширине не помещалась
              и уводила кнопку «Закрыть» за край.
            */}
              <th>Действует до</th>
              <th className="table__num">Открытий</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {links.map((link) => (
              <tr key={link.link_id}>
                <td>
                  {link.recipient ?? 'на счёт целиком'}
                  {/* Напечатанная в письме (20.10.2026): её закрытие или замена
                      оставит у должника на руках бумагу с мёртвой ссылкой - замены
                      у неё нет, а закрытие спрашивается окном. */}
                  {link.purpose === 'notice' ? (
                    <div className="muted">напечатана в уведомлении</div>
                  ) : null}
                </td>
                <td data-label="Действует до">{dateTime(link.expires_at)}</td>
                <td className="table__num" data-label="Открытий">
                  {link.opened_count}
                  <div className="muted">
                    {link.opened_count === 0
                      ? 'ещё не открывали'
                      : `последний раз ${dateTime(link.last_opened_at)}`}
                  </div>
                </td>
                <td>
                  {/*
                  Строка кнопок не переносится: две короткие подписи браузер
                  иначе ставит столбиком и вдвое удлиняет строку таблицы.
                */}
                  <div className="row row--tight">
                    {/*
                    «Выдать заново» вместо «Скопировать»: показать ту же ссылку
                    ещё раз невозможно - в базе только её хеш (§3.8.2). Кнопка
                    заменяет ссылку и кладёт новую в буфер обмена; о том, что
                    прежняя закроется, сказано выше таблицы - до нажатия.
                  */}
                    {/* Ссылку из уведомления не заменяют (20.10.2026): сервер
                        откажет, а замена погасила бы ссылку в отправленном письме. */}
                    {link.purpose === 'notice' ? null : (
                      <button
                        className="button button--quiet"
                        type="button"
                        disabled={busy}
                        onClick={() => onReissue(link)}
                      >
                        Выдать заново
                      </button>
                    )}
                    {/* Ссылку целиком, а не номер: у ссылки из уведомления закрытие
                        спрашивается окном (23.10.2026, `DebtorLinkCloseConfirm`). */}
                    <button
                      className="button button--quiet"
                      type="button"
                      disabled={busy}
                      onClick={() => onClose(link)}
                    >
                      Закрыть
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PageBar {...paging} what="ссылок" />
    </div>
  )
}
